import type { Currency } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import {
  Skeleton,
  UpdateHighlighter,
  type UpdateHighlighterProps,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { atom } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useMemo } from "react"
import { useUsdPrice } from "./hooks"
import { getPriceFormat } from "./utils"

interface Props {
  amount: string
  currency: Currency
  className?: string
  currencyClassName?: string
  format?: UpdateHighlighterProps["format"]
  /**
   * Min number that will be actually shown, if number less than value - label will be shown
   */
  minValuableNumber?: {
    value: number
    label: string
  }
  displayZeroAsFree?: boolean
}

export function PriceInCrypto({
  currency,
  className,
  amount,
  currencyClassName,
  format,
  displayZeroAsFree,
  minValuableNumber = {
    value: 0.0001,
    label: "<0.0001",
  },
}: Props) {
  if (currency === undefined) {
    return null
  }

  const priceFormat = useMemo(
    () => format ?? getPriceFormat(Number(amount)),
    [format, amount],
  )

  return (
    <span className={cn("text-nowrap", className)}>
      {amount === "0" && displayZeroAsFree ? (
        <Trans>FREE</Trans>
      ) : (
        <>
          {Number(amount) < minValuableNumber.value && amount !== "0" ? (
            minValuableNumber.label
          ) : (
            <UpdateHighlighter value={Number(amount)} format={priceFormat} />
          )}{" "}
          <span
            className={cn(
              "currency-symbol text-foreground/60 uppercase",
              currencyClassName,
            )}
          >
            {currency.abbreviation}
          </span>
        </>
      )}
    </span>
  )
}

export function PriceInUsd({
  currency,
  className,
  amount,
  currencyClassName,
  format,
  displayZeroAsFree,
  minValuableNumber = {
    value: 0.01,
    label: "<$0.01",
  },
}: Props) {
  const priceInUsd = useUsdPrice({ currency, amount })

  // If USD conversion not available, show empty value
  if (!priceInUsd && priceInUsd !== 0) {
    return (
      <span className={cn("text-nowrap text-foreground/60", className)}>—</span>
    )
  }

  const priceFormat = useMemo(
    () => format ?? getPriceFormat(priceInUsd),
    [format, priceInUsd],
  )

  return (
    <span className={cn("text-nowrap", className)}>
      {amount === "0" && displayZeroAsFree ? (
        <Trans>FREE</Trans>
      ) : (
        <>
          {priceInUsd < minValuableNumber.value && amount !== "0" ? (
            minValuableNumber.label
          ) : (
            <UpdateHighlighter
              value={priceInUsd}
              format={priceFormat}
              prefix="$"
            />
          )}
        </>
      )}
    </span>
  )
}

export function CryptoAndUsdPrices(props: Props) {
  return (
    <div className={cn("flex flex-col items-end", props.className)}>
      <PriceInUsd {...props} />
      <PriceInCrypto {...props} />
    </div>
  )
}

export function CryptoAndUsdPricesSkeleton() {
  return (
    <div className="flex flex-col items-end">
      <Skeleton className="h-6 w-20" />
      <Skeleton className="h-4 w-16" />
    </div>
  )
}

export enum PriceView {
  USD = "USD",
  NATIVE_CRYPTO = "NATIVE_CRYPTO",
}

// Global price view atom (shared across leaderboard instances)
export const priceView = atom(PriceView.USD, "priceView")

/**
 * Universal price component that switches between USD and crypto based on global priceView atom
 * Uses the same interface as PriceInCrypto and PriceInUsd
 */
export const PriceWrapper = reatomComponent((props: Props) => {
  const currentPriceView = priceView()

  if (currentPriceView === PriceView.USD) {
    return <PriceInUsd {...props} />
  }

  return <PriceInCrypto {...props} />
}, "PriceWrapper")
